import React, { useId, useState } from 'react';
import { Optional, Required } from '../FieldMarks';
import { useSettingsState } from '../../../../viewModels/useSettingsModel';
import AvatarPicker from './AvatarPicker';
import { ReviewersRows, TeamsRows } from './ProfileRows';
import './Profile.css';

const FIELDS = ['displayName', 'title', 'department', 'about', 'colour'];

const formOf = (p) => ({
  displayName: p.displayName,
  title: p.title,
  department: p.department,
  about: p.about,
  colour: p.colour,
  teams: p.teams,
  reviewers: p.reviewers,
});

const sameRows = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Settings > Profile (Navy Phase 0, story 30): the signed-in user, edited in
 * place. The role is shown, not edited: an administrator sets it. Everything
 * here, the avatar and the rows included, stages into one form; Update profile
 * commits it and Cancel walks it back, both disabled until something changes.
 */
function ProfileSection({ model, onShowToast }) {
  const { profile, profileRole } = useSettingsState(model);
  const ids = useId();
  const [form, setForm] = useState(() => formOf(profile));
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const changed = FIELDS.some((key) => form[key] !== profile[key])
    || !sameRows(form.teams, profile.teams)
    || !sameRows(form.reviewers, profile.reviewers);
  const complete = !!form.displayName.trim();

  const cancel = () => {
    setForm(formOf(profile));
    onShowToast?.('Changes discarded.', 'info');
  };

  const save = () => {
    if (!changed || !complete) return;
    setForm(formOf(model.saveProfile(form)));
    onShowToast?.('Profile saved.', 'success');
  };

  return (
    <>
      <section className="navy-settings-section" aria-labelledby={`${ids}-title`}>
        <div className="navy-settings-head">
          <h2 className="navy-settings-title" id={`${ids}-title`}>Your profile</h2>
        </div>
        <div className="library-panel">
          <AvatarPicker
            name={form.displayName}
            colour={form.colour}
            onColour={set('colour')}
            onShowToast={onShowToast}
          />
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-name`}>Display name<Required /></label>
            <input id={`${ids}-name`} type="text" className="resui-input" value={form.displayName} onChange={(e) => set('displayName')(e.target.value)} />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-job`}>Title<Optional /></label>
            <input id={`${ids}-job`} type="text" className="resui-input" value={form.title} onChange={(e) => set('title')(e.target.value)} />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-role`}>Role</label>
            <input id={`${ids}-role`} type="text" className="resui-input" value={profileRole} disabled aria-describedby={`${ids}-role-help`} />
            <span id={`${ids}-role-help`} className="resui-field-helper">Set by an administrator in Permissions</span>
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-dept`}>Department<Optional /></label>
            <input id={`${ids}-dept`} type="text" className="resui-input" value={form.department} onChange={(e) => set('department')(e.target.value)} />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-about`}>About me<Optional /></label>
            <textarea
              id={`${ids}-about`}
              className="resui-textarea profile-about"
              rows={4}
              placeholder="Shared with CodaAI to personalise its responses - the datasets you own, the questions you ask, your domain..."
              value={form.about}
              onChange={(e) => set('about')(e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="navy-settings-section" aria-labelledby={`${ids}-teams`}>
        <div className="navy-settings-head">
          <h2 className="navy-settings-title" id={`${ids}-teams`}>Teams</h2>
        </div>
        <div className="library-panel">
          <TeamsRows teams={form.teams} onChange={set('teams')} />
        </div>
      </section>

      <section className="navy-settings-section" aria-labelledby={`${ids}-reviewers`}>
        <div className="navy-settings-head">
          <h2 className="navy-settings-title" id={`${ids}-reviewers`}>Reviewers</h2>
        </div>
        <div className="library-panel">
          <ReviewersRows reviewers={form.reviewers} onChange={set('reviewers')} />
        </div>
      </section>

      <div className="navy-settings-actions">
        <button type="button" className="resui-btn resui-btn--secondary" disabled={!changed} onClick={cancel}>Cancel</button>
        <button type="button" className="resui-btn resui-btn--primary" disabled={!changed || !complete} onClick={save}>Update profile</button>
      </div>
    </>
  );
}

export default ProfileSection;
